<!--
  IGNTR sidebar nav. Each section (Main, CRM, Ignite, Lobby, Reception,
  Admin Panel, CRM Admin) follows the same pattern:
    1. a collapsible header button bound to `sidebarState.*Open`
    2. a list of <app-sidebar-row> items underneath

  All data sources, sidebarState toggles, active route detection, tenant
  switching and CRM-special-case logic are identical to the previous
  implementation. Only the visual structure changed.

  Item icons still come from `item.svgPath` (server-driven). They are dark
  by design, so we apply a `.sidebar-icon` filter that inverts them for
  the dark sidebar surface.
-->
<div class="flex flex-col gap-0.5">
  <!-- ============================ MAIN ============================ -->
  <!-- Hidden when the sidebar is in CRM-only mode (driven by the
       tenant's customTenantProperties.isFromCRM flag) so a CRM
       workspace shows only CRM / Lobby / Reception navigation. -->
  <ng-container *ngIf="filteredMainItems.length && !isFromCRM">
    <button
      *ngIf="collapse"
      type="button"
      (click)="toggleBar('isMainOpen')"
      [ngClass]="sectionHeaderClass(isSectionActive(allMainSideMenuItems) && !router.url.includes('admin'))"
    >
      <igntr-icon name="grid" [size]="14" class="opacity-70"></igntr-icon>
      <span class="flex-1 text-left">{{ "Main" }}</span>
      <span
        class="opacity-50 transition-transform"
        [class.rotate-90]="sidebarState.isMainOpen || hasSearch"
      >
        <igntr-icon name="chevronR" [size]="14"></igntr-icon>
      </span>
    </button>

    <div
      *ngIf="!collapse || sidebarState.isMainOpen || hasSearch"
      [class.section-children]="collapse"
    >
      <a
        *ngFor="let item of filteredMainItems"
        [routerLink]="item.routerLink"
        (click)="menuclose(item)"
        [ngClass]="rowClass(test(item) && !router.url.includes('admin'))"
      >
        <span class="sidebar-icon-wrap">
          <span
            *ngIf="item.svgPath"
            class="sidebar-icon-img"
            [style.-webkit-mask-image]="'url(' + item.svgPath + ')'"
            [style.mask-image]="'url(' + item.svgPath + ')'"
          ></span>
        </span>
        <span class="flex-1 text-left truncate">
          {{ item.displayedName | transloco }}
        </span>
        <span
          *ngIf="item.notificationsCount"
          class="rounded-full bg-[var(--app-theme-color)] px-1.5 py-0.5 font-mono text-[10px] leading-none text-white"
        >
          {{ item.notificationsCount }}
        </span>
      </a>
    </div>
  </ng-container>

  <!-- ============================ CRM ============================ -->
  <ng-container *ngIf="crm && hasCrmMatches">
    <button
      type="button"
      (click)="onCrmHeaderClick()"
      [ngClass]="sectionHeaderClass(isSectionActive(allCRMSideMenuItems))"
    >
      <igntr-icon name="briefcase" [size]="14" class="opacity-70"></igntr-icon>
      <span class="flex-1 text-left">CRM</span>
      <span
        class="opacity-50 transition-transform"
        [class.rotate-90]="sidebarState.isCrmOpen || hasSearch"
      >
        <igntr-icon name="chevronR" [size]="14"></igntr-icon>
      </span>
    </button>

    <div *ngIf="sidebarState.isCrmOpen || hasSearch" class="section-children">
      <a
        *ngIf="matchesText('Dashboard')"
        routerLink="/CRM/dashboard"
        (click)="menuclose()"
        [ngClass]="rowClass(router.url.includes('/CRM/dashboard'))"
      >
        <span class="sidebar-icon-wrap">
          <i class="pi pi-th-large text-[14px]"></i>
        </span>
        <span class="flex-1 text-left">{{ "dashboard" | transloco }}</span>
      </a>
      <a
        *ngIf="matchesText('Deals')"
        routerLink="/CRM/deals"
        (click)="menuclose()"
        [ngClass]="rowClass(router.url.includes('/CRM/deals'))"
      >
        <span class="sidebar-icon-wrap">
          <i class="pi pi-dollar text-[14px]"></i>
        </span>
        <span class="flex-1 text-left">Deals</span>
      </a>
      <a
        *ngIf="matchesText('Accounts')"
        routerLink="/CRM/accounts"
        (click)="menuclose()"
        [ngClass]="rowClass(router.url.includes('/CRM/accounts'))"
      >
        <span class="sidebar-icon-wrap">
          <i class="pi pi-building text-[14px]"></i>
        </span>
        <span class="flex-1 text-left">Accounts</span>
      </a>
      <a
        *ngIf="matchesText('Contacts')"
        routerLink="/CRM/contacts"
        (click)="menuclose()"
        [ngClass]="rowClass(router.url.includes('/CRM/contacts'))"
      >
        <span class="sidebar-icon-wrap">
          <i class="pi pi-users text-[14px]"></i>
        </span>
        <span class="flex-1 text-left">Contacts</span>
      </a>
      <a
        *ngIf="matchesText('Tasks')"
        routerLink="/CRM/tasks"
        (click)="menuclose()"
        [ngClass]="rowClass(router.url.includes('/CRM/tasks'))"
      >
        <span class="sidebar-icon-wrap">
          <i class="pi pi-check-square text-[14px]"></i>
        </span>
        <span class="flex-1 text-left">Tasks</span>
      </a>
    </div>
  </ng-container>

  <!-- ========================= GAMIFICATION ========================= -->
  <ng-container *ngIf="gamificationEnabled && !isFromCRM && matchesText('Gamification')">
    <a
      routerLink="/gamification"
      (click)="menuclose()"
      [ngClass]="sectionHeaderClass(router.url.includes('/gamification'))"
    >
      <i class="pi pi-star text-[14px] opacity-70"></i>
      <span class="flex-1 text-left">{{ "gamification" | transloco }}</span>
    </a>
    <div *ngIf="isAnyAdmin" class="section-children">
      <a
        routerLink="/gamification/members"
        (click)="menuclose()"
        [ngClass]="rowClass(router.url.includes('/gamification/members'))"
      >
        <span class="sidebar-icon-wrap">
          <i class="pi pi-users text-[14px]"></i>
        </span>
        <span class="flex-1 text-left">{{ "members" | transloco }}</span>
      </a>
      <a
        routerLink="/gamification/admin"
        (click)="menuclose()"
        [ngClass]="rowClass(router.url.includes('/gamification/admin'))"
      >
        <span class="sidebar-icon-wrap">
          <i class="pi pi-cog text-[14px]"></i>
        </span>
        <span class="flex-1 text-left">{{ "configuration" | transloco }}</span>
      </a>
    </div>
  </ng-container>

  <!-- ============================ IGNITE ============================ -->
  <!-- Same isFromCRM gate as Main section. -->
  <ng-container *ngIf="filteredIgniteItems.length && !isFromCRM">
    <button
      type="button"
      (click)="toggleBar('isIgniteOpen')"
      [ngClass]="sectionHeaderClass(isSectionActive(allIgniteSideMenuItems))"
    >
      <igntr-icon name="sparkle" [size]="14" class="opacity-70"></igntr-icon>
      <span class="flex-1 text-left">Ignite</span>
      <span
        class="opacity-50 transition-transform"
        [class.rotate-90]="sidebarState.isIgniteOpen || hasSearch"
      >
        <igntr-icon name="chevronR" [size]="14"></igntr-icon>
      </span>
    </button>

    <div *ngIf="sidebarState.isIgniteOpen || hasSearch" class="section-children">
      <a
        *ngFor="let item of filteredIgniteItems"
        [routerLink]="item.routerLink"
        (click)="menuclose()"
        [ngClass]="rowClass(test(item))"
      >
        <span class="sidebar-icon-wrap">
          <span
            *ngIf="item.svgPath"
            class="sidebar-icon-img"
            [style.-webkit-mask-image]="'url(' + item.svgPath + ')'"
            [style.mask-image]="'url(' + item.svgPath + ')'"
          ></span>
        </span>
        <span class="flex-1 text-left truncate">{{ item.displayedName | transloco }}</span>
      </a>
    </div>
  </ng-container>

  <!-- ============================ LOBBY ============================ -->
  <ng-container *ngIf="isFromCRM && filteredLobbyItems.length">
    <button
      type="button"
      (click)="toggleBar('isLobbyOpen')"
      [ngClass]="sectionHeaderClass(isSectionActive(allLobbySideMenuItems))"
    >
      <igntr-icon name="building" [size]="14" class="opacity-70"></igntr-icon>
      <span class="flex-1 text-left">Lobby</span>
      <span
        class="opacity-50 transition-transform"
        [class.rotate-90]="sidebarState.isLobbyOpen || hasSearch"
      >
        <igntr-icon name="chevronR" [size]="14"></igntr-icon>
      </span>
    </button>
    <div *ngIf="sidebarState.isLobbyOpen || hasSearch" class="section-children">
      <a
        *ngFor="let item of filteredLobbyItems"
        [routerLink]="item.routerLink"
        (click)="menuclose()"
        [ngClass]="rowClass(test(item))"
      >
        <span class="sidebar-icon-wrap">
          <span
            *ngIf="item.svgPath"
            class="sidebar-icon-img"
            [style.-webkit-mask-image]="'url(' + item.svgPath + ')'"
            [style.mask-image]="'url(' + item.svgPath + ')'"
          ></span>
        </span>
        <span class="flex-1 text-left truncate">{{ item.displayedName | transloco }}</span>
      </a>
    </div>
  </ng-container>

  <!-- ============================ RECEPTION ============================ -->
  <ng-container *ngIf="isFromCRM && filteredReceptionItems.length">
    <button
      type="button"
      (click)="toggleBar('isReception')"
      [ngClass]="sectionHeaderClass(isSectionActive(allReceptionSideMenuItems))"
    >
      <igntr-icon name="inbox" [size]="14" class="opacity-70"></igntr-icon>
      <span class="flex-1 text-left">Reception</span>
      <span
        class="opacity-50 transition-transform"
        [class.rotate-90]="sidebarState.isReception || hasSearch"
      >
        <igntr-icon name="chevronR" [size]="14"></igntr-icon>
      </span>
    </button>
    <div *ngIf="sidebarState.isReception || hasSearch" class="section-children">
      <a
        *ngFor="let item of filteredReceptionItems"
        [routerLink]="item.routerLink"
        (click)="menuclose()"
        [ngClass]="rowClass(test(item))"
      >
        <span class="sidebar-icon-wrap">
          <span
            *ngIf="item.svgPath"
            class="sidebar-icon-img"
            [style.-webkit-mask-image]="'url(' + item.svgPath + ')'"
            [style.mask-image]="'url(' + item.svgPath + ')'"
          ></span>
        </span>
        <span class="flex-1 text-left truncate">{{ item.displayedName | transloco }}</span>
      </a>
    </div>
  </ng-container>

  <!-- ============================ ADMIN ============================ -->
  <ng-container *ngIf="filteredAdminItems.length">
    <button
      type="button"
      (click)="toggleBar('isAdminOpen')"
      [ngClass]="sectionHeaderClass(isSectionActive(allAdminSideMenuItems))"
    >
      <igntr-icon name="settings" [size]="14" class="opacity-70"></igntr-icon>
      <span class="flex-1 text-left">{{ "adminPanel" | transloco }}</span>
      <span
        class="opacity-50 transition-transform"
        [class.rotate-90]="sidebarState.isAdminOpen || hasSearch"
      >
        <igntr-icon name="chevronR" [size]="14"></igntr-icon>
      </span>
    </button>
    <div *ngIf="sidebarState.isAdminOpen || hasSearch" class="section-children">
      <a
        *ngFor="let item of filteredAdminItems"
        [routerLink]="item.routerLink"
        (click)="menuclose()"
        [ngClass]="rowClass(test(item))"
      >
        <span class="sidebar-icon-wrap">
          <span
            *ngIf="item.svgPath"
            class="sidebar-icon-img"
            [style.-webkit-mask-image]="'url(' + item.svgPath + ')'"
            [style.mask-image]="'url(' + item.svgPath + ')'"
          ></span>
        </span>
        <span class="flex-1 text-left truncate">{{ item.displayedName | transloco }}</span>
      </a>
    </div>
  </ng-container>

  <!-- ============================ CRM ADMIN ============================ -->
  <ng-container *ngIf="crm && allAdminSideMenuItems.length && hasCrmAdminMatches">
    <button
      type="button"
      (click)="toggleBar('isCrmAdminOpen')"
      [ngClass]="sectionHeaderClass(isSectionActive(allCRMAdminSideMenuItems))"
    >
      <igntr-icon name="key" [size]="14" class="opacity-70"></igntr-icon>
      <span class="flex-1 text-left">CRM Admin</span>
      <span
        class="opacity-50 transition-transform"
        [class.rotate-90]="sidebarState.isCrmAdminOpen || hasSearch"
      >
        <igntr-icon name="chevronR" [size]="14"></igntr-icon>
      </span>
    </button>
    <div *ngIf="sidebarState.isCrmAdminOpen || hasSearch" class="section-children">
      <a *ngIf="matchesText('Task Types')" routerLink="/CRM/taskTypes" (click)="menuclose()" [ngClass]="rowClass(router.url.includes('/CRM/taskTypes'))">
        <span class="sidebar-icon-wrap"><i class="pi pi-bookmark text-[14px]"></i></span>
        <span class="flex-1 text-left">Task Types</span>
      </a>
      <a *ngIf="matchesText('Tags')" routerLink="/CRM/tags" (click)="menuclose()" [ngClass]="rowClass(router.url.includes('/CRM/tags'))">
        <span class="sidebar-icon-wrap"><i class="pi pi-hashtag text-[14px]"></i></span>
        <span class="flex-1 text-left">Tags</span>
      </a>
      <a *ngIf="matchesText('Lists')" routerLink="/CRM/lists" (click)="menuclose()" [ngClass]="rowClass(router.url.includes('/CRM/lists'))">
        <span class="sidebar-icon-wrap"><i class="pi pi-list text-[14px]"></i></span>
        <span class="flex-1 text-left">Lists</span>
      </a>
      <a *ngIf="matchesText('Custom Fields')" routerLink="/CRM/customFields" (click)="menuclose()" [ngClass]="rowClass(router.url.includes('/CRM/customFields'))">
        <span class="sidebar-icon-wrap"><i class="pi pi-sliders-h text-[14px]"></i></span>
        <span class="flex-1 text-left">{{ "customFields" | transloco }}</span>
      </a>
      <a *ngIf="matchesText('Score Rules')" routerLink="/CRM/scoreRules" (click)="menuclose()" [ngClass]="rowClass(router.url.includes('/CRM/scoreRules'))">
        <span class="sidebar-icon-wrap"><i class="pi pi-bolt text-[14px]"></i></span>
        <span class="flex-1 text-left">{{ "scoreRules" | transloco }}</span>
      </a>
    </div>
  </ng-container>

  <!-- ====================== AI ASSISTANT ====================== -->
  <!-- Collapsible group: the AI Advanced Pilot page plus the
       user-owned Pilot Accounts / Contacts surfaced for tenants
       that don't use the full CRM. Replaces the standalone
       top-level "AI Advanced Pilot" entry. -->
  <ng-container *ngIf="aiAssistantEnabled && (matchesText('AI Assistant') || matchesText('Pilot') || matchesText('Accounts') || matchesText('Contacts'))">
    <button
      type="button"
      (click)="toggleBar('isAiAssistantOpen')"
      [ngClass]="sectionHeaderClass(router.url.includes('/ai-assistant') || router.url.includes('/ai-advanced-pilot'))"
      class="mt-2"
    >
      <igntr-icon name="brain" [size]="14" class="opacity-70"></igntr-icon>
      <span class="flex-1 text-left">{{ "aiAssistant" | transloco }}</span>
      <span
        class="opacity-50 transition-transform"
        [class.rotate-90]="sidebarState.isAiAssistantOpen || hasSearch"
      >
        <igntr-icon name="chevronR" [size]="14"></igntr-icon>
      </span>
    </button>
    <div *ngIf="sidebarState.isAiAssistantOpen || hasSearch" class="section-children">
      <a
        *ngIf="matchesText('Pilot')"
        routerLink="/ai-assistant/pilot"
        (click)="menuclose()"
        [ngClass]="rowClass(router.url.includes('/ai-assistant/pilot') || router.url.includes('/ai-advanced-pilot'))"
      >
        <span class="sidebar-icon-wrap">
          <igntr-icon name="brain" [size]="14"></igntr-icon>
        </span>
        <span class="flex-1 text-left">{{ "pilot" | transloco }}</span>
      </a>
      <a
        *ngIf="matchesText('Accounts')"
        routerLink="/ai-assistant/accounts"
        (click)="menuclose()"
        [ngClass]="rowClass(router.url.includes('/ai-assistant/accounts'))"
      >
        <span class="sidebar-icon-wrap"><i class="pi pi-briefcase text-[14px]"></i></span>
        <span class="flex-1 text-left">{{ "accounts" | transloco }}</span>
      </a>
      <a
        *ngIf="matchesText('Contacts')"
        routerLink="/ai-assistant/contacts"
        (click)="menuclose()"
        [ngClass]="rowClass(router.url.includes('/ai-assistant/contacts'))"
      >
        <span class="sidebar-icon-wrap"><i class="pi pi-users text-[14px]"></i></span>
        <span class="flex-1 text-left">{{ "contacts" | transloco }}</span>
      </a>
    </div>
  </ng-container>
</div>

<!-- Tenant switcher moved to the parent <app-sidebar> template so it stays
     pinned above the user card (outside the scrollable nav). The
     `tenantsOptions` field + `setActiveTenant` method on this component
     remain unused for backwards-compat in case any other view references
     them. -->

